/**
 * camera.js — how much of the arena is on screen, and where it is centred.
 *
 * Until now the view was static: the whole 800x600 play area, always, sitting
 * in the middle of whatever frame the device gave it. That is safe and it is
 * inert — the picture never responds to the fight happening in it.
 *
 * This pushes in by default and pulls back only as far as it must to keep both
 * fighters on screen. Closing on someone tightens the frame around you; a board
 * kicked the length of the arena widens it out. The camera is doing what a
 * commentator's zoom does, which is telling you where the fight is.
 *
 * Two hard constraints shape every number below.
 *
 * BOTH FIGHTERS ARE ALWAYS VISIBLE, in both axes. A fighting game that hides
 * your opponent is broken, so the fit-both rule always wins over the preferred
 * zoom. This is the rule that does the work: at full push-in the arena is
 * bigger than the frame, so the camera is genuinely tracking rather than
 * sitting still with everything comfortably inside it.
 *
 * THE FRAMING IS SAID IN WORLD PIXELS, NOT IN ZOOM. The frame's size is the
 * device's, not ours, so a zoom number does not describe a shot — the same 1.4x
 * is a tight two-shot on a 4:3 window and the whole arena plus scenery on an
 * ultrawide. Every limit here is therefore "how much arena may be on screen",
 * and the zoom that delivers it is worked out per frame size.
 *
 * THE PLAY BAND IS THE SUBJECT, not a hard boundary. The camera prefers to show
 * the whole of it — containment rail down to deck — and gives that up when the
 * fighters need the space, panning vertically to follow them instead.
 *
 * Everything here is a pure function of the two players and the frame. The
 * online guest computes the identical camera from the snapshot it already has,
 * so none of it crosses the wire.
 */

import {
  CEILING_Y, GROUND_Y, BODY_TOP, BODY_BOTTOM, BODY_HALF_WIDTH,
  screenWidth, START_SCREEN,
} from './constants';

/**
 * The tightest the camera will ever frame, given as the world rectangle it
 * refuses to go inside — NOT as a scale factor.
 *
 * This distinction is the whole of it. The frame is not a fixed size: viewport.js
 * grows it to the device's shape, up to 1.9x wide, so one zoom number shows a
 * different amount of world on every screen. The old fixed ceiling of 1.4x meant
 * 594 world px of arena on a 4:3 window and 1024 on an ultrawide — wider than
 * the entire 800px arena. With the whole arena already in frame, the fit-both
 * rule below could essentially never fall under the ceiling, so the zoom sat
 * pinned there for whole matches and the camera never moved. The push-in was in
 * the code and had nowhere to travel.
 *
 * Said as world extents it means the same thing everywhere: however wide the
 * frame, the closest shot shows this much arena and no less.
 *
 * Both extents have to be satisfied, so on a wide-but-short frame it is the
 * height that binds — which is right. An ultrawide has spare width and no spare
 * height; pushing past it would crop the play band without revealing anything.
 */
const CLOSEST_VIEW_W = 500;

/*
 * There is no floor on the pull-back, and that is deliberate.
 *
 * There used to be a MIN_ZOOM of 0.75, justified as "past this the fighters are
 * too small to read". It was a hard floor that outranked the fit-both rule, so
 * on paper it could hide a fighter — it just never bound, because the arena was
 * narrow enough that fitting both never asked for less. Now that the outer
 * screens are more than twice as wide it WOULD bind, and it would break the one
 * guarantee this module exists to keep.
 *
 * The room itself is the floor. Two fighters cannot be further apart than the
 * screen is wide, so the pull-back is bounded by the widest screen and needs no
 * separate limit: at the widest room on the narrowest frame it bottoms out near
 * 0.43. If fighters ever get too small to read, the answer is a narrower room,
 * not a camera that stops showing one of them.
 */

/**
 * The vertical band that must always be on screen, in world coordinates.
 *
 * Top is the containment rail rather than the ceiling clamp, because the rail
 * is what a player aims at; bottom is a little under the deck so the floor line
 * never sits flush against the edge of the screen.
 */
const BAND_TOP = CEILING_Y + BODY_TOP - 30;
const BAND_BOTTOM = GROUND_Y + 26;
const BAND_HEIGHT = BAND_BOTTOM - BAND_TOP;

/**
 * Vertical half of the closest framing (see CLOSEST_VIEW_W), as a share of the
 * band rather than a number of its own — so it is stated as how much of the
 * arena the tightest shot is allowed to crop, and it follows the band if the
 * arena's height is ever retuned.
 *
 * This share is what the containment cell can survive, and it was found by
 * looking rather than by reasoning. A landscape frame is 600 world px tall no
 * matter how wide the screen is, so the vertical is the only place a push-in
 * can come from — and past roughly this much crop the cell's roof leaves the
 * frame in an ordinary grounded two-shot. Tried at 0.60 the box stopped reading
 * as a box at all: two side rails and a floor line, with the trussed roof that
 * closes it gone. The arena has to stay a room, so the push-in stops here.
 */
const CLOSEST_VIEW_H = BAND_HEIGHT * 0.72;

/**
 * Clear space kept outside the fighters when framing them, in world px.
 *
 * A camera that fits two fighters exactly to the edges puts them both where
 * they cannot see what is arriving, and reframes violently on every step. Less
 * vertical than horizontal because the arena is far shorter than it is wide —
 * the same margin up and down would dominate the framing.
 *
 * Horizontally the margin TIGHTENS as the fighters separate, and it is worth
 * being clear about why, because it is the difference between a readable
 * fighter and a speck on the widest screens. The margin buys reaction room:
 * space to see what is coming at you before it arrives. At arm's length that is
 * worth a lot and costs nothing. At a room's length apart nothing is arriving
 * suddenly — you can see them coming the whole way — so the margin buys
 * nothing, and it is being paid for at the exact moment the camera is most
 * pulled back and every world pixel is most expensive. On an 1800-wide screen
 * holding the full margin at maximum separation costs about 10% of the
 * fighters' on-screen height for no gain.
 */
const FIGHT_MARGIN = 160;
const FIGHT_MARGIN_FAR = 80;
/** Gap over which the horizontal margin closes from FIGHT_MARGIN to _FAR. */
const MARGIN_TIGHTENS_OVER = 700;
const FIGHT_MARGIN_V = 70;

/**
 * The margin that is not negotiable, in world px. FIGHT_MARGIN above is what
 * the camera AIMS for; this is what it is held to when smoothing cannot keep
 * up. Small on purpose — its only job is that a fighter is never flush against
 * the edge of the screen.
 */
const HARD_MARGIN = 20;

/**
 * Per-frame approach rates for pan and zoom, as a fraction of the remaining
 * distance. Zoom is slower than pan: a camera that re-zooms as fast as it
 * tracks reads as breathing, and it is the single most nauseating thing a 2D
 * camera can do.
 *
 * The two zoom directions are NOT the same job, and treating them as one was a
 * bug. Pushing in is a choice — nothing goes wrong if it takes a moment — so it
 * stays slow, and slow is what keeps the framing calm. Pulling back is not a
 * choice: it is the "both fighters visible" guarantee arriving, and arriving
 * late means a fighter is off the edge of the screen in the meantime. At one
 * rate for both, a fighter sprinting for the far wall outran the zoom and spent
 * a few frames outside the frame — measured at up to 29px past the edge, worse
 * on the widest screens, because the camera was still easing back when they got
 * there. So pulling back is roughly four times faster than pushing in.
 *
 * The asymmetry is also what stops it breathing: an oscillation needs both
 * directions to be quick, and the return is deliberately not.
 */
const PAN_RATE = 0.11;
const ZOOM_IN_RATE = 0.045;
const ZOOM_OUT_RATE = 0.2;

/**
 * Of the frame height left over once the play band is on screen, how much goes
 * above it. Only has any effect when the camera is pulled back further than the
 * band needs — i.e. in the big rooms, at long separations.
 */
const SKY_SHARE = 0.68;

/** A camera at rest on the landing stage, showing the whole arena. */
export function createCamera() {
  return {
    x: screenWidth(START_SCREEN) / 2,
    y: (BAND_TOP + BAND_BOTTOM) / 2,
    zoom: 1,
  };
}

/**
 * Where the camera would like to be this instant, before smoothing.
 *
 * @param {object} a First player.
 * @param {object} b Second player.
 * @param {number} frameW Frame width in canvas px.
 * @param {number} frameH Frame height in canvas px.
 * @param {number} roomW Width of the screen they are fighting in, world px.
 *   Not a constant: the outer screens are more than twice the landing stage,
 *   and a camera that assumed one width would clamp its pan to the wrong
 *   middle and stop following the fight halfway across the bigger rooms.
 * @param {number} [insetTop] Frame px along the top that something opaque
 *   covers — the phone's match header. The fight is framed in the rest of the
 *   frame, so a rider at the ceiling is never behind the HUD; the returned
 *   centre is still the WHOLE frame's, which is what the renderer uses.
 * @returns {{x: number, y: number, zoom: number}}
 */
export function cameraTarget(a, b, frameW, frameH, roomW, insetTop = 0) {
  // Everything below frames into the uncovered part of the frame, and converts
  // back to the whole frame's centre at the end.
  const fullH = frameH;
  frameH = Math.max(1, fullH - insetTop);
  // Both fighters visible, in BOTH axes. Horizontal alone was enough while the
  // zoom was gentle: a landscape frame still showed the whole band, so vertical
  // could never be the binding constraint. Pushed in far enough to crop the band
  // it is — one rider at the ceiling and one on the deck are 380px apart, which
  // no longer fits — and without this the camera happily framed one of them off
  // the top of the screen.
  const gap = Math.abs(a.x - b.x);
  const closeness = Math.min(1, gap / MARGIN_TIGHTENS_OVER);
  const marginX = FIGHT_MARGIN + (FIGHT_MARGIN_FAR - FIGHT_MARGIN) * closeness;

  const spreadX = gap + marginX * 2;
  const spreadY = Math.abs(a.y - b.y) - BODY_TOP + FIGHT_MARGIN_V * 2;
  const fitBoth = Math.min(frameW / spreadX, frameH / spreadY);

  // The tightest framing THIS frame allows, derived rather than fixed — see
  // CLOSEST_VIEW_W for why a constant zoom ceiling silently disabled the
  // push-in on anything wider than a 4:3 window.
  const maxZoom = Math.min(frameW / CLOSEST_VIEW_W, frameH / CLOSEST_VIEW_H);

  const zoom = Math.min(maxZoom, fitBoth);

  // Centre on the fight, then pull back inside the room so the camera never
  // wanders off into scenery the fighters cannot reach.
  const visibleW = frameW / zoom;
  const visibleH = frameH / zoom;
  const arenaW = roomW + 60;
  const middle = roomW / 2;
  const midX = (a.x + b.x) / 2;

  let x = middle;
  if (visibleW < arenaW) {
    const slack = (arenaW - visibleW) / 2;
    x = Math.max(middle - slack, Math.min(middle + slack, midX));
  }

  // Vertically the band is the subject, but the camera follows the fighters
  // within whatever room is left over — which at this zoom is most of the time,
  // and is what finally lets the framing move on a landscape phone.
  const bandMid = (BAND_TOP + BAND_BOTTOM) / 2;
  let y = bandMid;
  if (visibleH < BAND_HEIGHT) {
    const slack = (BAND_HEIGHT - visibleH) / 2;
    const midY = (a.y + b.y) / 2 - 30;
    y = Math.max(bandMid - slack, Math.min(bandMid + slack, midY));
  } else {
    // More height on screen than the band needs — which is what a wide room
    // does to a uniform zoom: pulling back far enough to fit 1800px of arena
    // across also shows nearly twice the play band's height, and the band is
    // all there is to see. Splitting that surplus evenly puts as much bare
    // deck under the floor line as sky over the roof, and the deck is a flat
    // black slab while the sky has the city in it. So the surplus is weighted
    // upward: the fight sits low in a tall frame and the room it opens is
    // skyline. Costs nothing — the eye stays on the horizon through the whole
    // fight, so this is a pan, not a change of perspective.
    y = bandMid - (visibleH - BAND_HEIGHT) * (SKY_SHARE - 0.5);
  }
  // From the uncovered region's centre to the whole frame's.
  y -= (fullH - frameH) / (2 * zoom);

  // The world rectangle that MUST be on screen, carried alongside the framing
  // so the smoothing can be held to it — see enforceVisible.
  const fit = {
    left: Math.min(a.x, b.x) - BODY_HALF_WIDTH - HARD_MARGIN,
    right: Math.max(a.x, b.x) + BODY_HALF_WIDTH + HARD_MARGIN,
    top: Math.min(a.y, b.y) + BODY_TOP - HARD_MARGIN,
    bottom: Math.max(a.y, b.y) + BODY_BOTTOM + HARD_MARGIN,
  };

  return { x, y, zoom, fit };
}

/**
 * Force a camera to show a world rectangle, in place.
 *
 * This exists because easing TOWARD a safe framing is not the same as being
 * safely framed, and for a while this module quietly assumed it was. Every
 * frame's target does keep both fighters on screen — but the camera only ever
 * travels a fraction of the way there, and when two fighters sprint apart the
 * target moves faster than the smoothing follows. Measured on a 4:3 window: a
 * fighter reaching the far wall sat up to 29px outside the frame for several
 * frames while the camera was still pulling back. Tuning the rates made it
 * rarer and could not make it impossible, because it is a lag, and lag is what
 * smoothing IS.
 *
 * So the guarantee is enforced rather than approached: ease for feel, then
 * clamp. The clamp only bites on the frames that were broken, and moves the
 * camera the minimum distance that fixes them.
 */
function enforceVisible(cam, fit, frameW, frameH, insetTop = 0) {
  // The guarantee is about the part of the frame a player can SEE, so a
  // covered strip along the top does not count as showing anything.
  const seenH = Math.max(1, frameH - insetTop);
  const needW = fit.right - fit.left;
  const needH = fit.bottom - fit.top;
  // Wide enough to hold them, in both axes.
  cam.zoom = Math.min(cam.zoom, frameW / needW, seenH / needH);

  // Then centred somewhere that actually contains them. Both intervals are
  // non-empty by construction, since the zoom above already made the window at
  // least as big as the rectangle. Vertically that is the SEEN window's centre,
  // which sits below the frame's by half the covered strip.
  const halfW = frameW / cam.zoom / 2;
  const halfH = seenH / cam.zoom / 2;
  const drop = insetTop / cam.zoom / 2;
  cam.x = Math.max(fit.right - halfW, Math.min(fit.left + halfW, cam.x));
  cam.y = Math.max(fit.bottom - halfH, Math.min(fit.top + halfH, cam.y + drop)) - drop;
}

/**
 * Ease a camera toward its target, in place.
 *
 * @param {object} cam Camera to advance.
 * @param {object} target From cameraTarget. If it carries a `fit` rectangle,
 *   the result is held to it — see enforceVisible. The victory camera has no
 *   such rectangle, because by then there is nobody left to keep in frame.
 * @param {number} frameW Frame width in canvas px.
 * @param {number} frameH Frame height in canvas px.
 * @param {boolean} [snap] Jump straight there — used on a screen change, where
 *   easing would look like the camera sliding across the city.
 * @param {number} [insetTop] Covered strip along the top (see cameraTarget).
 */
export function advanceCamera(cam, target, frameW, frameH, snap = false, insetTop = 0) {
  if (snap) {
    cam.x = target.x;
    cam.y = target.y;
    cam.zoom = target.zoom;
  } else {
    cam.x += (target.x - cam.x) * PAN_RATE;
    cam.y += (target.y - cam.y) * PAN_RATE;
    // Pulling back is the visibility guarantee catching up; pushing in is taste.
    const rate = target.zoom < cam.zoom ? ZOOM_OUT_RATE : ZOOM_IN_RATE;
    cam.zoom += (target.zoom - cam.zoom) * rate;
  }
  if (target.fit) enforceVisible(cam, target.fit, frameW, frameH, insetTop);
}

/**
 * Convert a world point to frame coordinates under a camera.
 *
 * The single definition of the camera's transform. The renderer applies it to
 * the context, the pointer path inverts it, and the tests read it — all from
 * here, so they cannot disagree about where anything is.
 *
 * @returns {{x: number, y: number}}
 */
export function worldToFrame(cam, frameW, frameH, wx, wy) {
  return {
    x: frameW / 2 + (wx - cam.x) * cam.zoom,
    y: frameH / 2 + (wy - cam.y) * cam.zoom,
  };
}

/** Inverse of worldToFrame. */
export function frameToWorld(cam, frameW, frameH, fx, fy) {
  return {
    x: cam.x + (fx - frameW / 2) / cam.zoom,
    y: cam.y + (fy - frameH / 2) / cam.zoom,
  };
}
